/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { CONTENT_MAX_WIDTH_AUTH } from '../../shared/layout/breakpoints';
import { AuthScreenFrame } from '../../shell/brand/AuthScreenFrame';
import { AppLogoMark } from '../../shell/brand/AppLogoMark';
import { showSuccess } from '../../shared/ui/appToast';
import { employeeInvitationEmailConfirmCopy } from '@shared/employeeInvitationEmailConfirmCopy';
import { resendSignupConfirmation } from '../application/authCredentials';
import { AUTH_ACCENT_INK, authFormUi } from './components/authFormUi';
import { AuthWhatsAppHelpLink } from './components/AuthWhatsAppHelpLink';

const copy = employeeInvitationEmailConfirmCopy.negocioPending;

type Props = {
  email: string;
  /** Vuelve al login con el correo cargado. */
  onBackToLogin: () => void;
};

/**
 * Post-signup sin sesión: Auth pide confirmar el correo antes de entrar (B0-05).
 */
export const EmailConfirmationPendingScreen = ({ email, onBackToLogin }: Props) => {
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResend = useCallback(async () => {
    setError(null);
    setResending(true);
    try {
      await resendSignupConfirmation(email);
      showSuccess(copy.resendSuccessTitle, copy.resendSuccessBody);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'No pudimos reenviar el enlace.');
    } finally {
      setResending(false);
    }
  }, [email]);

  return (
    <AuthScreenFrame>
      <View style={styles.root} accessibilityLabel="Confirmá tu correo">
        <View style={[authFormUi.card, styles.cardMax]}>
          <View style={authFormUi.brandBlock}>
            <AppLogoMark size={56} showWordmark />
          </View>

          <View style={styles.head}>
            <Text style={authFormUi.title}>{copy.title}</Text>
            <Text style={authFormUi.subtitle}>{copy.subtitle(email)}</Text>
          </View>

          {error ? (
            <View style={authFormUi.errorBanner} accessibilityLiveRegion="polite">
              <Text style={authFormUi.errorBannerText}>{error}</Text>
            </View>
          ) : null}

          <TouchableOpacity
            style={[authFormUi.primaryBtn, resending && authFormUi.primaryBtnDisabled]}
            onPress={() => void handleResend()}
            disabled={resending}
            accessibilityRole="button"
            accessibilityLabel="Reenviar enlace"
            testID="auth.resend-confirmation"
          >
            {resending ? (
              <ActivityIndicator color={AUTH_ACCENT_INK} />
            ) : (
              <Text style={authFormUi.primaryLabel}>{copy.resendCta}</Text>
            )}
          </TouchableOpacity>

          <View style={authFormUi.links}>
            <TouchableOpacity
              onPress={onBackToLogin}
              disabled={resending}
              accessibilityRole="button"
              accessibilityLabel="Ya lo confirmé, ingresar"
              testID="auth.confirmed-back-to-login"
            >
              <Text style={authFormUi.linkLabel}>{copy.backCta}</Text>
            </TouchableOpacity>
            <AuthWhatsAppHelpLink testID="auth.confirm-whatsapp" muted={false} />
          </View>
        </View>
      </View>
    </AuthScreenFrame>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cardMax: {
    maxWidth: CONTENT_MAX_WIDTH_AUTH,
  },
  head: {
    gap: 8,
  },
});
